import { useEffect, useState } from "react";
import axios from "axios";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link, useNavigate } from "react-router-dom";
import { Filter, Car, Fuel, Settings2, Calculator } from "lucide-react";
import FinancingCalculator from "@/components/FinancingCalculator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const VehiclesPage = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [financingDialog, setFinancingDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles`);
        setVehicles(response.data);
        setFilteredVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    let filtered = [...vehicles];
    
    if (brandFilter !== "all") {
      filtered = filtered.filter(v => v.brand === brandFilter);
    }
    
    if (typeFilter !== "all") {
      filtered = filtered.filter(v => 
        typeFilter === "new" ? v.is_new : !v.is_new
      );
    }
    
    setFilteredVehicles(filtered);
  }, [brandFilter, typeFilter, vehicles]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-black py-16" data-testid="vehicles-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white normal-case tracking-tight mb-4">
            Unsere Fahrzeuge
          </h1>
          <div className="h-1 w-16 bg-[#EFDF00] mb-6" />
          <p className="text-[#D9D9D6] max-w-2xl">
            Entdecken Sie unsere große Auswahl an Renault und Dacia Fahrzeugen. 
            Ob Neuwagen oder geprüfter Gebrauchtwagen – wir haben das richtige Auto für Sie.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b" data-testid="vehicles-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2 text-black font-medium">
              <Filter className="w-5 h-5" />
              <span>Filter:</span>
            </div>
            
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-[180px]" data-testid="brand-filter">
                <SelectValue placeholder="Marke wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Marken</SelectItem>
                <SelectItem value="Renault">Renault</SelectItem>
                <SelectItem value="Dacia">Dacia</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="type-filter">
                <SelectValue placeholder="Fahrzeugtyp" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Typen</SelectItem>
                <SelectItem value="new">Neuwagen</SelectItem>
                <SelectItem value="used">Gebrauchtwagen</SelectItem>
              </SelectContent>
            </Select>

            <div className="ml-auto text-sm text-[#888B8D]">
              {filteredVehicles.length} Fahrzeuge gefunden
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Grid */}
      <section className="py-12" data-testid="vehicles-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#EBEBEB] h-96 animate-pulse border border-[#D9D9D6]" />
              ))}
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-16">
              <Car className="w-16 h-16 text-[#D9D9D6] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-black mb-2">Keine Fahrzeuge gefunden</h3>
              <p className="text-[#888B8D]">Bitte passen Sie Ihre Filter an.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVehicles.map((vehicle) => (
                <Card 
                  key={vehicle.id} 
                  className={`overflow-hidden border border-[#D9D9D6] shadow-none hover:border-black transition-colors cursor-pointer ${vehicle.is_sold ? 'opacity-75' : ''}`}
                  data-testid={`vehicle-card-${vehicle.id}`}
                  onClick={() => navigate(`/fahrzeuge/${vehicle.id}`)}
                >
                  <div className="relative h-56 overflow-hidden bg-[#EBEBEB]">
                    <img 
                      src={vehicle.image_url} 
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className={`w-full h-full object-cover ${vehicle.is_sold ? 'grayscale' : ''}`}
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      {vehicle.is_sold ? (
                        <span className="bg-red-600 text-white px-3 py-1 text-xs font-bold normal-case">
                          Verkauft
                        </span>
                      ) : vehicle.is_new ? (
                        <span className="bg-[#EFDF00] text-black px-3 py-1 text-xs font-bold normal-case">
                          Neuwagen
                        </span>
                      ) : (
                        <span className="bg-black text-white px-3 py-1 text-xs font-bold normal-case">
                          Gebrauchtwagen
                        </span>
                      )}
                    </div>
                    {!vehicle.is_sold && (
                      <div className="absolute top-4 right-4 bg-white px-3 py-1">
                        <span className="text-black font-bold">
                          {vehicle.price.toLocaleString('de-DE')} €
                        </span>
                      </div>
                    )}
                    {vehicle.is_sold && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-600 text-white px-6 py-3 text-xl font-bold normal-case">
                          Verkauft
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <p className="text-sm text-[#888B8D] font-medium mb-1">{vehicle.brand}</p>
                      <h3 className="text-2xl font-bold text-black">
                        {vehicle.model}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <Car className="w-4 h-4" />
                        <span>{vehicle.year}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <Settings2 className="w-4 h-4" />
                        <span>{vehicle.transmission}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <Fuel className="w-4 h-4" />
                        <span>{vehicle.fuel_type}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <span>km</span>
                        <span>{vehicle.mileage.toLocaleString('de-DE')}</span>
                      </div>
                    </div>

                    {vehicle.features && vehicle.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {vehicle.features.slice(0, 3).map((feature, i) => (
                          <span 
                            key={i} 
                            className="bg-[#F5F5F5] text-[#888B8D] px-2 py-1 text-xs"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    {vehicle.is_sold ? (
                      <Button 
                        disabled
                        className="w-full bg-[#D9D9D6] text-[#888B8D] cursor-not-allowed"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Nicht mehr verfügbar
                      </Button>
                    ) : (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <Link 
                          to="/kontakt" 
                          state={{ 
                            vehicle: {
                              id: vehicle.id,
                              brand: vehicle.brand,
                              model: vehicle.model,
                              price: vehicle.price,
                              year: vehicle.year,
                              image_url: vehicle.image_url
                            }
                          }}
                        >
                          <Button 
                            className="w-full bg-black text-white hover:bg-[#333333] font-bold"
                            data-testid={`vehicle-inquiry-${vehicle.id}`}
                          >
                            Jetzt anfragen
                          </Button>
                        </Link>
                        <Button 
                          variant="outline"
                          className="w-full border-[#EFDF00] text-black hover:bg-[#EFDF00]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVehicle(vehicle);
                            setFinancingDialog(true);
                          }}
                          data-testid={`vehicle-financing-${vehicle.id}`}
                        >
                          <Calculator className="w-4 h-4 mr-2" />
                          Rate berechnen
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Financing Calculator Dialog */}
      <Dialog open={financingDialog} onOpenChange={setFinancingDialog}>
        <DialogContent className="max-w-md p-0 bg-transparent border-0">
          <FinancingCalculator 
            vehiclePrice={selectedVehicle?.price || 0}
            vehicleName={selectedVehicle ? `${selectedVehicle.brand} ${selectedVehicle.model} (${selectedVehicle.year})` : ""}
          />
        </DialogContent>
      </Dialog>

      {/* CTA Banner */}
      <section className="py-16 bg-[#646B52]" data-testid="vehicles-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white  normal-case mb-4">
            Nicht das Richtige dabei?
          </h2>
          <p className="text-[#D9D9D6] mb-8 max-w-2xl mx-auto">
            Kontaktieren Sie uns – wir helfen Ihnen, Ihr Traumauto zu finden oder bestellen es für Sie!
          </p>
          <Link to="/kontakt">
            <Button 
              className="bg-white text-black hover:bg-[#EBEBEB] font-bold px-8 py-6"
              data-testid="vehicles-contact-btn"
            >
              Kontakt aufnehmen
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VehiclesPage;
